import {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import theme from './ordersTheme.module.scss';
import ViewOrders from './ViewOrders/ViewOrders';
import {BrokerBackOffice} from './brokerBackOffice/BrokerBackOffice';
import {OrdersMetricsTab} from './OrdersMetrics/OrdersMetricsTab';

/**
 * The exchange list, update and delete that used to sit on a first "Exchanges"
 * tab here now live in their own section at /dashboard/exchanges, which also
 * owns narrative content and trading config. Create moved to that list's
 * header. This screen is orders and broker back office only.
 */
const tabs = [
  {key: 'BACK_OFFICE', label: 'Broker'},
  // Metrics is its own tab rather than a strip inside the queues because
  // `getOrdersMetrics` has no status filter — it reports every lifecycle
  // stage at once, so inside a status-scoped queue it would show identical
  // numbers on all six.
  {key: 'METRICS', label: 'Metrics'},
  {key: 'ACTIVE', label: 'Open Orders'},
  {key: 'PENDING', label: 'Pending Orders'},
  {key: 'FILLED', label: 'Filled Orders'},
  {key: 'HOLD', label: 'Orders on Hold'},
  {key: 'PAST', label: 'Past Orders'},
  {key: 'EXPIRED', label: 'Expired Orders'},
];

const ORDER_TABS = ['ACTIVE', 'PAST', 'PENDING', 'FILLED', 'HOLD', 'EXPIRED'];

const AdminStockListings = () => {
  const route = useLocation();
  const searchParamValue = new URLSearchParams(route.search).get('route');
  const [activeTab, setActiveTab] = useState('');
  // The queue reports how many orders its current filter matched, so the tab
  // in view can carry a count without six extra queries for the ones that
  // are not.
  const [activeCount, setActiveCount] = useState(null);
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(route.search);
  const searchInstrumentId = searchParams.get('instrumentId');

  useEffect(() => {
    if (searchParamValue === 'order') {
      setActiveTab('ACTIVE');
    } else if (searchParamValue === 'backOffice') {
      setActiveTab('BACK_OFFICE');
    } else if (searchParamValue) {
      setActiveTab(searchParamValue);
    } else setActiveTab('ACTIVE');
  }, [searchParamValue]);

  useEffect(() => {
    setActiveCount(null);
  }, [activeTab]);

  return (
    <div>
      <div className={theme.head}>
        <div className={theme.headTop}>
          <div>
            <h1 className={theme.title}>Orders</h1>
            <div className={theme.sub}>Trading View</div>
          </div>
          {searchInstrumentId && (
            <div className={theme.headActions}>
              <button
                type="button"
                className={theme.control}
                onClick={() => navigate(`?route=${activeTab}`)}>
                Clear instrument filter
              </button>
            </div>
          )}
        </div>

        <div className={theme.tabs} role="tablist">
          {tabs.map(tab => {
            const isActive = activeTab === tab.key;
            return (
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                key={tab.key}
                className={`${theme.tab} ${isActive ? theme.tabActive : ''}`}
                onClick={() => {
                  navigate(
                    `?route=${tab.key}${
                      searchInstrumentId
                        ? `&instrumentId=${searchInstrumentId}`
                        : ''
                    }`,
                  );
                }}>
                {tab.label}
                {isActive && activeCount != null && (
                  <span className={theme.tabCount}>
                    {activeCount.toLocaleString()}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        {activeTab === 'BACK_OFFICE' && <BrokerBackOffice />}
        {activeTab === 'METRICS' && <OrdersMetricsTab />}
        {ORDER_TABS.includes(activeTab) && (
          <ViewOrders activeTab={activeTab} onTotalChange={setActiveCount} />
        )}
      </div>
    </div>
  );
};

export {AdminStockListings};
